import type { RefObject } from 'react';

import { useEffect, useState } from 'react';

import { useDebouncedCallback } from './useDebouncedCallback.js';

interface OverflowState {
  isOverflowing: boolean;
  isOverflowingHeight: boolean;
  isOverflowingWidth: boolean;
}

/**
 * Detects if an element is overflowing its container in either width or height.
 *
 * Uses a debounced callback to prevent excessive re-renders.
 */
export const useIsOverflowing = <T extends HTMLElement>(
  ref: RefObject<T | null>,
): OverflowState => {
  const [overflowState, setOverflowState] = useState<OverflowState>({
    isOverflowing: false,
    isOverflowingWidth: false,
    isOverflowingHeight: false,
  });

  const checkOverflow = useDebouncedCallback(
    () => {
      if (!ref.current) return;

      const element = ref.current;
      const isOverflowingWidth = element.scrollWidth > element.clientWidth;
      const style = window.getComputedStyle(element);
      const lineHeight = parseFloat(style.lineHeight);
      const lineClamp = parseInt(style.getPropertyValue('-webkit-line-clamp'), 10);
      const isOverflowingHeight = Number.isFinite(lineClamp) && lineHeight > 0
        ? Math.round(element.scrollHeight / lineHeight) > lineClamp
        : element.scrollHeight > element.clientHeight;

      setOverflowState({
        isOverflowing: isOverflowingHeight || isOverflowingWidth,
        isOverflowingWidth,
        isOverflowingHeight,
      });
    },
    {
      delay: 100,
    },
  );

  useEffect(
    () => {
      // Initial check
      checkOverflow();

      // Create ResizeObserver to detect size changes, and MutationObserver to detect changes to the DOM
      const resizeObserver = new ResizeObserver(checkOverflow);
      const mutationObserver = new MutationObserver(checkOverflow);
      if (ref.current) {
        resizeObserver.observe(ref.current);
        mutationObserver.observe(ref.current, {
          attributes: true,
          characterData: true,
          childList: true,
          subtree: true,
        });
      }

      // Cleanup
      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 100% we need ref.current here - eslint is wrong
    [ref, ref.current, checkOverflow],
  );

  return overflowState;
};
